import { useEffect, useRef, useState } from "react";
import { DisplayMode, Status } from "./day/dayView";
import { DropZone } from "../elements/DropZone";
import { ObjectKind } from "../models/objectKinds";
import { MaterialItemDTO } from "../DTO/MaterialItemDTO";
import MaterialView from "./materialView";
import { GenericMaterialItemDTO } from "../DTO/GenericMaterialItemDTO";
import { ArrowRight, CheckSquare, Eye, File as FileIcon, Printer, Square } from "react-feather";
import { PublishKind } from "../models/publishKinds";
import Spinner from "../../components/Spinner/Spinner";
import { GenericDTO } from "../DTO/GenericDTO";
import InputB from "../../components/InputB/InputB";
import { Setter } from "../../services/ComService";
import { File } from "./FileManager/FileManager";
import './MaterialsView.scss';
import mainservice from "../../services/MainService";
import { RightDrawerContent } from "./RightDrawer";
import { EventDTO } from "../DTO/EventDTO";
import { TextAreaArraySummary } from "./Event/TextAreaArraySummary";
import { CountHolder } from "./CountHolder";
import { MakePrintLink } from "./MakePrintLink";
import { DisplayKind } from "../DTO/Materialkinds";
import { groupByPrintSettings, standardPrintCount, PrintSettingGroup } from "../DTO/PrintFormats";
import { printPdfWithQz, qzErrorMessage, QzPrintJob } from "../services/QzPrint";

export default function MaterialsView(props: {
  items: MaterialItemDTO[],
  identKey: string,
  parentPublishKind: PublishKind,
  parentObjectKind: ObjectKind,
  getItems?: () => Promise<MaterialItemDTO[]>,
  getItemsAtInit?: boolean,
  addItem?:(position: number, item: MaterialItemDTO) => void,
  trashItem?: (item: GenericMaterialItemDTO) => void,
  displayKind: DisplayKind,
  displayMode?: DisplayMode,
  parent?: GenericDTO,
  checklists?: {key1: string, title: string}[],
  reloadTrigger?: number,
  rerenderRightSide?: () => void,
  editItem?: (item: any, objecKind: ObjectKind) => void,
  headline?: string,
  className?: string,
  hideIfEmpty?: boolean
}) {
  const mounted = useRef(false);
  const loadItemsOnInit = props.getItems && props.getItemsAtInit !== false
  const [status, setStatus] = useState((props.getItems) ? Status.loading : Status.standard)
  const [items, setItems] = useState(loadItemsOnInit ? [] : props.items)
  const [rerender, setRerender] = useState(0)
  const [checklists, setChecklists] = useState(props.checklists)
  const [participantCount, setParticipantCount] = useState(0)
  const [appointment, setAppointment] = useState('')
  const [printCountHolder] = useState(new CountHolder({
    item: props.parent,
    key1: 'materialsCount',
    key2: 'print'
  }))
  const [packCountHolder] = useState(new CountHolder({
    item: props.parent,
    key1: 'materialsCount',
    key2: 'pack'
  }))
  function init() {
    if (status !== Status.loading) {
      setStatus(Status.loading)
    }
    if (loadItemsOnInit && props.getItems) {
      props.getItems().then((items: MaterialItemDTO[]) => {
        if (!mounted.current) { return }
        setItems(items)
        if (props.parent?.id) {
          if (props.displayKind === DisplayKind.list) {
            Setter('spt/event/personcount', {id: props.parent.id}).then(result => {
              setParticipantCount(result.participantCount)
              setStatus(Status.standard)
            })
          } else {
            setStatus(Status.standard)
          }
        } else {
          if (!mounted.current) { return }
          setStatus(Status.standard)
        }
      })
    } else {
      if (props.parent?.id && props.displayKind === DisplayKind.list) {
        Setter('spt/event/personcount', {id: props.parent.id}).then(result => {
          setParticipantCount(result.participantCount)
          setStatus(Status.standard)
        })
      } else {
        setStatus(Status.standard)
      }
    }
  }

  useEffect(() => {
    // Component is mounted, set the mounted.current value to true
    mounted.current = true;

    // Cleanup function when the component is unmounted
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    init()
  }, [props.reloadTrigger])
  
  if (status === Status.loading) {
    return <Spinner />
  }
  let appointments: {key2: string, name: string}[] = []
  if (props.parent && props.parent instanceof EventDTO) {
    appointments = [...[{key2: '', name: 'Alle'}], ...(props.parent as EventDTO).getAppointments().map((a, index) => {
      return {
        key2: a.key2,
        name: `Zeitraum ${index + 1}`
      }
    })]
  }

  if (props.displayKind === DisplayKind.list) {
    const filtered = items.filter(i => {
      if (!appointment) {
        return true
      }
      if (i.appointments.length === 0 || i.appointments.some(a => a === appointment)) {
        return true
      }
      return false
    })
    const printGroups = groupByPrintSettings(filtered.filter(i => i.getPropV1('data', 'printFormat')))
    const legacy = filtered.filter(i => !i.getPropV1('data', 'printFormat'))
    const flips = legacy.filter(i => i.getPropV1('data', 'materialKind') === 'digitalFlip')
    const handouts = legacy.filter(i => i.getPropV1('data', 'materialKind') === 'digitalHandout')
    const posters = legacy.filter(i => i.getPropV1('data', 'materialKind') === 'digitalPoster')
    const pins = legacy.filter(i => i.getPropV1('data', 'materialKind') === 'digitalPin')
    const collectibles = legacy.filter(i => i.getPropV1('data', 'materialKind') === 'collectible')
    const other = legacy.filter(i => 
      !flips.some(x => x.id === i.id) &&
      !handouts.some(x => x.id === i.id) &&
      !posters.some(x => x.id === i.id) &&
      !collectibles.some(x => x.id === i.id) &&
      !pins.some(x => x.id === i.id)
    )
    return <>
      {appointments.map((a, index) => {
        return <button
          title={a.key2}
          key={`appointment-tab-${a.key2}-${index}`}
          onClick={() => {
            setAppointment(a.key2)
          }}
          className={`tab-button-small ${a.key2 === appointment ? 'tab-active' : 'tab-not-active'}`}
        >{a.name}</button>
      })}
      { (!filtered || filtered.length === 0) &&
        <div className='empty'>Keine Materialien verfügbar</div>
      }
      {printGroups.map(group => <RenderList
        key={group.key}
        participantCount={participantCount}
        items={group.items}
        headline={group.headline}
        format={group.sizeToken}
        qz={group}
        checklists={checklists}
        setChecklists={(newChecklists) => setChecklists}
        parent={props.parent}
        printCountHolder={printCountHolder}
        packCountHolder={packCountHolder}
      />)}
      <RenderList
        participantCount={participantCount}
        items={flips}
        headline={'Flips'}
        format={'_670x990'}
        checklists={checklists}
        setChecklists={(newChecklists) => setChecklists}
        parent={props.parent}
        printCountHolder={printCountHolder}
        packCountHolder={packCountHolder}
      />
      <RenderList
        participantCount={participantCount}
        items={handouts}
        headline={'Handouts'}
        format={'_210x297'}
        checklists={checklists}
        setChecklists={(newChecklists) => setChecklists}
        parent={props.parent}
        printCountHolder={printCountHolder}
        packCountHolder={packCountHolder}
      />
      <RenderList
        participantCount={participantCount}
        items={posters}
        headline={'Plakate'}
        format={'_594x420'}
        checklists={checklists}
        setChecklists={(newChecklists) => setChecklists}
        parent={props.parent}
        printCountHolder={printCountHolder}
        packCountHolder={packCountHolder}
      />
      <RenderList
        participantCount={participantCount}
        items={pins}
        headline={'Pins'}
        format={'_670x990'}
        checklists={checklists}
        setChecklists={(newChecklists) => setChecklists}
        parent={props.parent}
        printCountHolder={printCountHolder}
        packCountHolder={packCountHolder}
      />
      <RenderList
        participantCount={participantCount}
        items={collectibles}
        headline={'Lernkarten'}
        format={'_500x500'}
        checklists={checklists}
        setChecklists={(newChecklists) => setChecklists}
        parent={props.parent}
        printCountHolder={printCountHolder}
        packCountHolder={packCountHolder}
      />
      <RenderList
        participantCount={participantCount}
        items={other}
        headline={'Sonstiges'}
        format={''}
        checklists={checklists}
        setChecklists={(newChecklists) => setChecklists}
        parent={props.parent}
        printCountHolder={printCountHolder}
        packCountHolder={packCountHolder}
      />
      {
        props.parent &&
        <TextAreaArraySummary
          item={props.parent}
          appointment={appointment}
        />
      }
    </>
  }
  if (props.hideIfEmpty && items.length === 0) {
    return null
  }
  return <div className={props.className || 'MaterialsView'}>
    {
      props.headline &&
      <h3>{props.headline}</h3>
    }
    <DropZone
      label={``}
      position={10000}
      addItem={async (position, item) => {
        if (props.addItem) {
          await props.addItem(position, item)
          if (props.getItems) {
            const items: MaterialItemDTO[] = await props.getItems()
            setItems(items)
          } else {
            setRerender(rerender + 1)
          }
        }
      }}
      acceptKind={ObjectKind.materialItem}
      className='ptdropzoneHorizontal ptdropBottomGeneric'
    />
    {
      items.map((item, index) => {
        return <MaterialView
          key={`material-items-view-${item.id}`}
          materialItem={item}
          parentObjectKind={props.parentObjectKind}
          parentPublishKind={props.parentPublishKind}
          displayMode={props.displayMode === undefined ? DisplayMode.leftSide : props.displayMode}
          position={index}
          trash={async () => {
            if (props.trashItem) {
              await props.trashItem(item as GenericMaterialItemDTO)
              if (props.getItems) {
                props.getItems().then((items: MaterialItemDTO[]) => {
                  setItems(items)
                  setStatus(Status.standard)
                })
              }
            }
          }}
          addItem={async (position, item) => {
            if (props.addItem) {
              await props.addItem(position, item)
            }
          }}
          fullScreenEdit={() => {
            if (props.editItem) {
              props.editItem(item, item.objectKind);
            }
          }}
          rerenderRightSide={props.rerenderRightSide}
          displayKind={props.displayKind === undefined ? undefined : props.displayKind}
        />
      })
    }
  </div>
}

function qzJobFromGroup(group: PrintSettingGroup<MaterialItemDTO>, url: string): QzPrintJob {
  return {
    url,
    formatValue: group.formatValue,
    formatTitle: group.formatTitle,
    widthMm: group.widthMm,
    heightMm: group.heightMm,
    orientation: group.orientation,
    sides: group.sides,
    color: group.color,
  }
}

function PrintActions(props: {
  items: MaterialItemDTO[],
  size: string,
  printCountHolder: CountHolder,
  standardCountHolder: Map<number, number>,
  qz?: PrintSettingGroup<MaterialItemDTO>,
  parent?: GenericDTO,
  compact?: boolean,
  onPrinted: () => void,
}) {
  const [busy, setBusy] = useState(false)
  const printLink = props.size ? MakePrintLink({
    flips: props.items,
    size: props.size,
    countHolder: props.printCountHolder,
    standardCountHolder: props.standardCountHolder,
  }) : ''
  const className = props.compact
    ? 'w3-button'
    : `${printLink ? 'neutralButton' : 'disabledButton'} `
  const markProduced = async () => {
    if (props.parent?.objectKind !== ObjectKind.event) { return }
    for (const item of props.items) {
      if (props.parent.getPropV1('checklist-produced', `${item.id}`) === '1') { continue }
      await props.parent.addProp('checklist-produced', `${item.id}`, '1')
    }
    props.onPrinted()
  }
  const runQz = async () => {
    if (!printLink || !props.qz || busy) { return }
    setBusy(true)
    try {
      await printPdfWithQz(qzJobFromGroup(props.qz, printLink))
      await markProduced()
    } catch (error) {
      window.alert(qzErrorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  if (!props.size) { return null }
  return <span className='mv-print-actions'>
    <a
      title={printLink ? 'Systemdruck' : 'Keine druckbaren Elemente'}
      className={className}
      href={printLink || '#'}
      target='_blank'
      rel='noreferrer'
    >
      <FileIcon /><Printer />
    </a>
    { props.qz &&
      <button
        type='button'
        title={printLink ? 'QZ Tray' : 'Keine druckbaren Elemente'}
        className={className}
        disabled={!printLink || busy}
        onClick={runQz}
      >
        <Printer /> QZ
      </button>
    }
  </span>
}

function RenderList(props: {
  items: MaterialItemDTO[],
  headline: string,
  format: string,
  participantCount: number,
  printCountHolder: CountHolder,
  packCountHolder: CountHolder,
  setChecklists: (newCheckList: {key1: string, title: string}[]) => void,
  parent?: GenericDTO,
  checklists?: {key1: string, title: string}[],
  qz?: PrintSettingGroup<MaterialItemDTO>,
}) {
  const [rerender, setRerender] = useState(0)
  // const [countHolder] = useState(new Map())
  const printCountHolder = props.printCountHolder
  const packCountHolder = props.packCountHolder
  const standardCountHolder = new Map<number, number>()
  const list = props.items.map((item, index) => {
    const hints = item.getPropV1('data', 'hints')
    const transportWarning = item.getPropV1('data', 'transportHints')
    const productionHints = item.getPropV1('data', 'productionHints')
    const count = standardPrintCount(item, props.participantCount)
    standardCountHolder.set(item.id, count)
    const currentFile = item.getCurrentFile()
    // const className='PTListItem flex flex-wrap justify-content-space-between'
    const className='PTListItem materialsView'
    return <tr
        key={`materialsList-${index}-${item.id}`}
        className={className}
      >
        { count !== -1 &&
          <>
            <td
              className='w3-padding mv-print-count'
              title='berechnete Anzahl'
            >{count}</td>
            <td
              className='w3-padding mv-print-count'
              title='Druck-Anzahl'
            >
              <InputB
                value={printCountHolder.has(item.id) ? printCountHolder.get(item.id) : count }
                returnVal={(value) => {
                  if (value === '') {
                    printCountHolder.delete(item.id)
                    setRerender(rerender + 1)
                  } else {
                    printCountHolder.set(item.id, parseInt(value, 10))
                    setRerender(rerender + 1)
                  }
                }}
              />
            </td>
            <td
              className='w3-padding mv-print-count'
              title='Pack-Anzahl'
            >
              <InputB
                value={packCountHolder.has(item.id) ? packCountHolder.get(item.id) : count }
                returnVal={(value) => {
                  if (value === '') {
                    packCountHolder.delete(item.id)
                    setRerender(rerender + 1)
                  } else {
                    packCountHolder.set(item.id, parseInt(value, 10))
                    setRerender(rerender + 1)
                  }
                }}
              />
            </td>
          </>
        }
        <td className='flex-grow w3-padding'>
          <div className='bold'>{item.getFirstTagNameByKind('materialNameTag')}</div>
          <div>{item.getName()}</div>
          <div className='credentials fontWeightNormal w3-margin-top'>
            {
              item.credentials.map(
                (c, index) => <span
                  key={`credentials-${item.id}-${index}`}
                  className={`credential-type-${item.type}`}
                >
                  { index > 1 &&
                    <ArrowRight size='12'/>
                  }
                  {index > 0 && c}
                </span>
              )
            }
          </div>
        </td>
        <td>
        { currentFile &&
            <File
              key={`single-file-view-${currentFile.id}`}
              showBiggerPreview={false}
              prop={currentFile}
              className={`flex-column w3-padding textRight`}
            />
          }
        </td>
        <td className='mv-last-column'>
          {
            transportWarning &&
            <button className='w3-button w3-padding w3-yellow'
              title='Transporthinweise beachten'
            >
              🚚
            </button>
          }
          {
            productionHints &&
            <button className='w3-button w3-padding w3-purple'
              title='Produktionshinweise beachten'
            >🔨</button>
          }
          {
            hints &&
            <button className='w3-button w3-padding w3-blue'
              title='Hinweise beachten'
            >⚠️</button>
          }
        
        {
          props.parent?.objectKind === ObjectKind.event &&
          props.checklists && props.parent &&
          props.checklists.map((c, index) => {
            const state = props.parent?.getPropV1(c.key1, `${item.id}`) === '1'
            const icon = state ? <CheckSquare /> : <Square />
            return <button
              title={c.title}
              key={`checklist-${c.key1}-${item.id}`}
              className='w3-button'
              onClick={async () => {
                await props.parent?.addProp(c.key1, `${item.id}`, state ? '0' : '1')
                setRerender(rerender + 1)
              }}
            >{icon}</button>
          })
        }
        
          <PrintActions
            items={[item]}
            size={props.format}
            printCountHolder={printCountHolder}
            standardCountHolder={standardCountHolder}
            qz={props.qz}
            parent={props.parent}
            compact
            onPrinted={() => setRerender(value => value + 1)}
          />
          <button
            className='w3-button'
            onClick={() => {
              mainservice.broadcast('rightDrawerOpen', {
                id: item.id,
                contentType: RightDrawerContent.viewMaterial,
              })
            }}
          ><Eye /></button>
        </td>
      </tr>
  })
  if (list.length === 0) {
    return null
  }
  return <div className='contentPart'>
    <div className='contentHeader'>
      <h2>{props.headline}</h2>
    </div>
    <table className='w100'>
      {list}
    </table>
    {
      props.format &&
      <div className='flex'>
        <div className='flex-grow'></div>
        <PrintActions
          items={props.items}
          size={props.format}
          printCountHolder={printCountHolder}
          standardCountHolder={standardCountHolder}
          qz={props.qz}
          parent={props.parent}
          onPrinted={() => setRerender(value => value + 1)}
        />
      </div>
    }
  </div>
}
